import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const AccountingContext = createContext();

export const useAccounting = () => {
  const context = useContext(AccountingContext);
  if (!context) {
    throw new Error('useAccounting must be used within AccountingProvider');
  }
  return context;
};

export const AccountingProvider = ({ children }) => {
  const [expenses, setExpenses] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Expense categories
  const expenseCategories = [
    'Raw Materials',
    'Utilities',
    'Rent',
    'Salaries',
    'Marketing',
    'Packaging',
    'Transportation',
    'Equipment',
    'Maintenance',
    'Insurance',
    'License & Permits',
    'Miscellaneous'
  ];

  // Load expenses
  useEffect(() => {
    const q = query(
      collection(db, 'expenses'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const expenseData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExpenses(expenseData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading expenses:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load ledgers
  useEffect(() => {
    const q = query(
      collection(db, 'ledgers'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ledgerData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLedgers(ledgerData);
    }, (error) => {
      console.error('Error loading ledgers:', error);
    });

    return () => unsubscribe();
  }, []);

  // Load bank accounts
  useEffect(() => {
    const q = query(collection(db, 'bank_accounts'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accountData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBankAccounts(accountData);
    }, (error) => {
      console.error('Error loading bank accounts:', error);
    });

    return () => unsubscribe();
  }, []);

  // Add expense
  const addExpense = async (expenseData) => {
    try {
      const newExpense = {
        ...expenseData,
        date: expenseData.date || Timestamp.now(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'expenses'), newExpense);

      // Create ledger entry
      await createLedgerEntry({
        type: 'debit',
        category: 'expense',
        amount: expenseData.amount,
        description: `Expense: ${expenseData.description}`,
        reference: docRef.id,
        expenseCategory: expenseData.category
      });

      console.log('✅ Expense added:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('❌ Error adding expense:', error);
      return { success: false, error: error.message };
    }
  };

  // Update expense
  const updateExpense = async (expenseId, updates) => {
    try {
      await updateDoc(doc(db, 'expenses', expenseId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Expense updated:', expenseId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating expense:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete expense
  const deleteExpense = async (expenseId) => {
    try {
      await deleteDoc(doc(db, 'expenses', expenseId));
      console.log('✅ Expense deleted:', expenseId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting expense:', error);
      return { success: false, error: error.message };
    }
  };

  // Create ledger entry
  const createLedgerEntry = async (entryData) => {
    try {
      const newEntry = {
        ...entryData,
        date: entryData.date || Timestamp.now(),
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'ledgers'), newEntry);
      return { success: true };
    } catch (error) {
      console.error('❌ Error creating ledger entry:', error);
      return { success: false, error: error.message };
    }
  };

  // Add bank account
  const addBankAccount = async (accountData) => {
    try {
      const docRef = await addDoc(collection(db, 'bank_accounts'), {
        ...accountData,
        balance: accountData.openingBalance || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('✅ Bank account added:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('❌ Error adding bank account:', error);
      return { success: false, error: error.message };
    }
  };

  // Update bank account
  const updateBankAccount = async (accountId, updates) => {
    try {
      await updateDoc(doc(db, 'bank_accounts', accountId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating bank account:', error);
      return { success: false, error: error.message };
    }
  };

  // Calculate P&L
  const calculateProfitLoss = (startDate, endDate) => {
    const start = startDate ? new Date(startDate) : new Date(new Date().setDate(1));
    const end = endDate ? new Date(endDate) : new Date();

    // Filter ledgers within date range
    const relevantLedgers = ledgers.filter(entry => {
      const entryDate = entry.date?.toDate ? entry.date.toDate() : new Date(entry.date);
      return entryDate >= start && entryDate <= end;
    });

    // Calculate revenue (credit entries)
    const revenue = relevantLedgers
      .filter(entry => entry.type === 'credit' && entry.category !== 'liability')
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);

    // Calculate expenses (debit entries for expenses)
    const expenseAmount = expenses
      .filter(exp => {
        const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
        return expDate >= start && expDate <= end;
      })
      .reduce((sum, exp) => sum + (exp.amount || 0), 0);

    // Calculate cost of goods sold (if tracked)
    const cogs = relevantLedgers
      .filter(entry => entry.type === 'debit' && entry.category === 'cogs')
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);

    const grossProfit = revenue - cogs;
    const netProfit = grossProfit - expenseAmount;

    // Expense breakdown by category
    const expenseBreakdown = {};
    expenseCategories.forEach(category => {
      expenseBreakdown[category] = expenses
        .filter(exp => {
          const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
          return exp.category === category && expDate >= start && expDate <= end;
        })
        .reduce((sum, exp) => sum + (exp.amount || 0), 0);
    });

    return {
      revenue: parseFloat(revenue.toFixed(2)),
      cogs: parseFloat(cogs.toFixed(2)),
      grossProfit: parseFloat(grossProfit.toFixed(2)),
      expenses: parseFloat(expenseAmount.toFixed(2)),
      netProfit: parseFloat(netProfit.toFixed(2)),
      profitMargin: revenue > 0 ? parseFloat(((netProfit / revenue) * 100).toFixed(2)) : 0,
      expenseBreakdown
    };
  };

  // Calculate cash flow
  const calculateCashFlow = (startDate, endDate) => {
    const start = startDate ? new Date(startDate) : new Date(new Date().setDate(1));
    const end = endDate ? new Date(endDate) : new Date();

    const relevantLedgers = ledgers.filter(entry => {
      const entryDate = entry.date?.toDate ? entry.date.toDate() : new Date(entry.date);
      return entryDate >= start && entryDate <= end;
    });

    const cashIn = relevantLedgers
      .filter(entry => entry.type === 'credit')
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);

    const cashOut = relevantLedgers
      .filter(entry => entry.type === 'debit')
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);

    const netCashFlow = cashIn - cashOut;

    // Opening balance (sum of all bank accounts)
    const openingBalance = bankAccounts.reduce(
      (sum, account) => sum + (account.openingBalance || 0),
      0
    );

    const closingBalance = openingBalance + netCashFlow;

    return {
      cashIn: parseFloat(cashIn.toFixed(2)),
      cashOut: parseFloat(cashOut.toFixed(2)),
      netCashFlow: parseFloat(netCashFlow.toFixed(2)),
      openingBalance: parseFloat(openingBalance.toFixed(2)),
      closingBalance: parseFloat(closingBalance.toFixed(2))
    };
  };

  // Get expense statistics
  const getExpenseStats = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const monthlyExpenses = expenses.filter(exp => {
      const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
      return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
    });

    const totalMonthlyExpenses = monthlyExpenses.reduce(
      (sum, exp) => sum + (exp.amount || 0),
      0
    );

    // Group by category
    const byCategory = {};
    expenseCategories.forEach(category => {
      byCategory[category] = monthlyExpenses
        .filter(exp => exp.category === category)
        .reduce((sum, exp) => sum + (exp.amount || 0), 0);
    });

    return {
      totalExpenses: expenses.length,
      monthlyExpenses: monthlyExpenses.length,
      totalMonthlyAmount: parseFloat(totalMonthlyExpenses.toFixed(2)),
      byCategory
    };
  };

  const value = {
    expenses,
    ledgers,
    bankAccounts,
    loading,
    expenseCategories,
    addExpense,
    updateExpense,
    deleteExpense,
    createLedgerEntry,
    addBankAccount,
    updateBankAccount,
    calculateProfitLoss,
    calculateCashFlow,
    getExpenseStats
  };

  return (
    <AccountingContext.Provider value={value}>
      {children}
    </AccountingContext.Provider>
  );
};
